import React, { useState, useMemo } from 'react';
import ShiftTable from './ShiftTable';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function monthLabel(value) {
  const [y, m] = value.split('-').map(Number);
  return m >= 1 && m <= 12 ? `${MONTHS[m - 1]} ${y}` : value;
}

const BLANK = { month: '', from: '', to: '', property: '', normalizedTag: '' };

// Does this shift survive the filters? `except` drops one field so a dropdown can
// count without counting itself — that is what keeps its options non-empty.
// Passing 'date' drops the whole date group (month + range), which are alternative
// ways of saying the same thing and are never both active.
function matches(s, fl, except) {
  const skip = k => k === except || (except === 'date' && (k === 'month' || k === 'range'));
  const shdate = s.shdate || '';
  if (!skip('month') && fl.month && shdate.slice(0, 7) !== fl.month) return false;
  if (!skip('range') && fl.from && shdate < fl.from) return false;
  if (!skip('range') && fl.to && shdate > fl.to) return false;
  if (!skip('property') && fl.property && (s.property || '') !== fl.property) return false;
  if (!skip('normalizedTag') && fl.normalizedTag && (s.normalizedTag || '') !== fl.normalizedTag) return false;
  return true;
}

// [{ value, label, count }] for one dropdown, counted against the other filters.
function optionsFor(field, get, shifts, filters, except) {
  const counts = new Map();
  shifts.forEach(s => {
    if (!matches(s, filters, except)) return;
    const v = get(s);
    if (v) counts.set(v, (counts.get(v) || 0) + 1);
  });
  const rows = [...counts.entries()].map(([value, count]) => ({ value, count }));
  if (field === 'month') rows.sort((a, b) => b.value.localeCompare(a.value));
  else rows.sort((a, b) => a.value.localeCompare(b.value));
  return rows;
}

const money = n => `$${(n || 0).toFixed(2)}`;

/**
 * ShiftTable plus a two-row filter bar: month / property / tag dropdowns that show
 * how many shifts sit behind each option, and an inclusive date range.
 * All other props pass straight through to ShiftTable.
 */
export default function FilterableShiftTable({ shifts = [], billingType, ...tableProps }) {
  const [fl, setFl] = useState(BLANK);

  const monthOpts = useMemo(
    () => optionsFor('month', s => (s.shdate || '').slice(0, 7), shifts, fl, 'date'),
    [shifts, fl]);
  const propOpts = useMemo(
    () => optionsFor('property', s => s.property || '', shifts, fl, 'property'),
    [shifts, fl]);
  const tagOpts = useMemo(
    () => optionsFor('normalizedTag', s => s.normalizedTag || '', shifts, fl, 'normalizedTag'),
    [shifts, fl]);

  const filtered = useMemo(() => shifts.filter(s => matches(s, fl)), [shifts, fl]);

  // Per-shift clients see what the selection is worth; monthly shifts carry no price.
  const totals = useMemo(() => {
    let billed = 0, unpaid = 0;
    filtered.forEach(s => {
      if (s.canceled || s.noCharge) return;
      const rate = s.rate || 0;
      billed += rate;
      if (!s.paid) unpaid += rate;
    });
    const r2 = n => Math.round(n * 100) / 100;
    return { billed: r2(billed), unpaid: r2(unpaid) };
  }, [filtered]);

  // Earliest and latest shift on file, so the date pickers cannot wander outside it.
  const bounds = useMemo(() => {
    const dates = shifts.map(s => s.shdate).filter(Boolean).sort();
    return { min: dates[0] || '', max: dates[dates.length - 1] || '' };
  }, [shifts]);

  // Month and range are two ways of scoping by date, so setting one clears the other.
  const setMonth = v => setFl(p => ({ ...p, month: v, from: '', to: '' }));
  const setRange = (k, v) => setFl(p => ({ ...p, [k]: v, month: '' }));

  const active = Object.values(fl).some(Boolean);

  const selectStyle = {
    flex: '1 1 90px', minWidth: 0, padding: '5px 6px', fontSize: 12,
    borderRadius: 4, border: '1px solid #e2e8f0', background: '#fff'
  };
  const dateStyle = {
    flex: '1 1 96px', minWidth: 0, padding: '4px 6px', fontSize: 12,
    borderRadius: 4, border: '1px solid #e2e8f0', background: '#fff'
  };

  return (
    <>
      <div style={{ background: '#f7f8fa', borderRadius: 6, padding: 8, marginBottom: 8 }}>
        {/* Row 1 — what kind of shift */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <select value={fl.month} onChange={e => setMonth(e.target.value)} style={selectStyle}>
            <option value="">All months ({monthOpts.reduce((n, o) => n + o.count, 0)})</option>
            {monthOpts.map(o => <option key={o.value} value={o.value}>{monthLabel(o.value)} ({o.count})</option>)}
          </select>
          <select value={fl.property} onChange={e => setFl(p => ({ ...p, property: e.target.value }))} style={selectStyle}>
            <option value="">All properties ({propOpts.reduce((n, o) => n + o.count, 0)})</option>
            {propOpts.map(o => <option key={o.value} value={o.value}>{o.value} ({o.count})</option>)}
          </select>
          <select value={fl.normalizedTag} onChange={e => setFl(p => ({ ...p, normalizedTag: e.target.value }))} style={selectStyle}>
            <option value="">Shift Type ({tagOpts.reduce((n, o) => n + o.count, 0)})</option>
            {tagOpts.map(o => <option key={o.value} value={o.value}>{o.value} ({o.count})</option>)}
          </select>
        </div>

        {/* Row 2 — when, and what the selection comes to */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#718096', flex: '0 0 auto' }}>Dates</span>
          <input
            type="date" value={fl.from} min={bounds.min} max={bounds.max}
            onChange={e => setRange('from', e.target.value)}
            style={dateStyle} aria-label="From date"
          />
          <span style={{ fontSize: 12, color: '#a0aec0', flex: '0 0 auto' }}>–</span>
          <input
            type="date" value={fl.to} min={fl.from || bounds.min} max={bounds.max}
            onChange={e => setRange('to', e.target.value)}
            style={dateStyle} aria-label="To date"
          />
          {active && (
            <button
              type="button"
              onClick={() => setFl(BLANK)}
              style={{
                flex: '0 0 auto', padding: '4px 8px', fontSize: 12, borderRadius: 4,
                border: '1px solid #e2e8f0', background: '#fff', color: '#718096', cursor: 'pointer'
              }}
            >Clear</button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#718096', whiteSpace: 'nowrap' }}>
            <strong style={{ color: '#1a1a2e' }}>{filtered.length}</strong>
            {filtered.length !== shifts.length && <> / {shifts.length}</>}
            {billingType === 'perShift' && totals.billed > 0 && (
              <>
                {' · '}<strong style={{ color: '#1a1a2e' }}>{money(totals.billed)}</strong>
                {totals.unpaid > 0 && <span style={{ color: '#e53e3e' }}> ({money(totals.unpaid)} due)</span>}
              </>
            )}
          </span>
        </div>
      </div>

      {filtered.length === 0
        ? <p style={{ color: '#a0aec0', fontSize: 13, margin: '8px 0' }}>No shifts match these filters.</p>
        : <ShiftTable shifts={filtered} billingType={billingType} {...tableProps} />}
    </>
  );
}
